import React from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { WebSocketProvider } from "@/contexts/WebSocketContext";
import { ThemeProvider } from "@/contexts/ThemeContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import PermissionRoute from "@/components/PermissionRoute";
import Layout from "@/components/Layout";
import ManagerPendingActionsWrapper from "@/components/ManagerPendingActionsWrapper";
import RealTimeNotificationListener from "@/components/RealTimeNotificationListener";

// Auth pages
import Login from "@/modules/auth/Login";
import Register from "@/modules/auth/Register";
import ForgotPassword from "@/modules/auth/ForgotPassword";
import ResetPassword from "@/modules/auth/ResetPassword";
import TwoFactorVerification from "@/modules/auth/TwoFactorVerification";

// Main modules
import Dashboard from "@/modules/dashboard/Dashboard";
import SimpleDashboard from "@/modules/dashboard/SimpleDashboard";
import AuditTrailPage from "@/modules/dashboard/AuditTrailPage";
import Projects from "@/modules/projects/Projects";
import ProjectDetail from "@/modules/projects/ProjectDetail";
import ProjectForm from "@/modules/projects/ProjectForm";
import EnhancedProjectForm from "@/modules/projects/EnhancedProjectForm";
import ProjectDashboard from "@/modules/projects/ProjectDashboard";
import ProjectKanban from "@/modules/projects/ProjectKanban";
import ProjectCalendar from "@/modules/projects/ProjectCalendar";
import ProjectReports from "@/modules/projects/ProjectReports";
import ProjectGanttReport from "@/modules/projects/ProjectGanttReport";
import ProjectTemplates from "@/modules/projects/ProjectTemplates";
import ProjectAnalytics from "@/modules/projects/ProjectAnalytics";
import ProjectArchive from "@/modules/projects/ProjectArchive";
import ProjectTasks from "@/modules/projects/ProjectTasks";
import ProjectTeam from "@/modules/projects/ProjectTeam";
import ProjectTimeline from "@/modules/projects/ProjectTimeline";
import ProjectBudget from "@/modules/projects/ProjectBudget";
import ProjectSettings from "@/modules/projects/ProjectSettings";
import Inventory from "@/modules/inventory/Inventory";
import HR from "@/modules/hr/HR";
import Finance from "@/modules/finance/Finance";
import Admin from "@/modules/admin/Admin";
import SelfService from "@/modules/self-service/SelfService";
import Collaboration from "@/modules/collaboration/Collaboration";
import FileShare from "@/modules/file-share/FileShare";
import EnhancedDocumentManagement from "@/modules/documents/EnhancedDocumentManagement";
import Email from "@/modules/email/Email";
import EmailPreview from "@/pages/EmailPreview";

// Support pages
import MyTickets from "@/pages/Support/MyTickets";
import CreateTicket from "@/pages/Support/CreateTicket";
import AllSupports from "@/pages/Support/AllSupports";

// Purchase Request pages
import {
  PurchaseRequestDashboard,
  PurchaseRequestList,
  PurchaseRequestForm,
  PurchaseRequestDetail,
} from "@/pages/PurchaseRequest";
import PurchaseRequestKanban from "@/components/PurchaseRequest/PurchaseRequestKanban";
import PurchaseRequestSettings from "@/modules/purchase-requests/components/Settings";
import PurchaseRequestHistory from "@/components/PurchaseRequest/PurchaseRequestHistory";
import ManagerApproval from "@/components/PurchaseRequest/ManagerApproval";
import ProcurementApproval from "@/components/PurchaseRequest/ProcurementApproval";
import FinancePayment from "@/components/PurchaseRequest/FinancePayment";

// GMD Module
import GMDDashboard from "@/modules/gmd/components/GMDDashboard";
import GMDSignature from "@/modules/gmd/components/GMDSignature";
import TransactionPayment from "@/modules/finance/TransactionPayment";
import GMDPin from "@/modules/gmd/components/GMDPin";

// Error pages
import NotFound from "@/components/NotFound";
import ErrorBoundary from "@/components/ErrorBoundary";

// Public pages
import PublicJobApplication from "@/pages/PublicJobApplication";
import PublicProbationReview from "@/pages/PublicProbationReview";
import GMDNotificationPreview from "@/pages/GMDNotificationPreview";

function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider>
        <AuthProvider>
          <ManagerPendingActionsWrapper>
            <WebSocketProvider>
              <RealTimeNotificationListener />
              <Routes>
                {/* Public routes */}
                <Route path="/login" element={<Login />} />
                <Route path="/verify-2fa" element={<TwoFactorVerification />} />
                <Route path="/register" element={<Register />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route
                  path="/careers/apply/:publicLink"
                  element={<PublicJobApplication />}
                />
                <Route
                  path="/probation/review/:publicLink"
                  element={<PublicProbationReview />}
                />
                <Route path="/email-preview" element={<EmailPreview />} />
                <Route path="/gmd-notification-preview" element={<GMDNotificationPreview />} />

                {/* Protected routes */}
                <Route
                  path="/"
                  element={
                    <ProtectedRoute>
                      <Layout />
                    </ProtectedRoute>
                  }
                >
                  <Route
                    index
                    element={<Navigate to="/self-service" replace />}
                  />
                  <Route path="dashboard" element={<Dashboard />} />
                  <Route
                    path="simple-dashboard"
                    element={<SimpleDashboard />}
                  />
                  <Route path="audit-trail" element={<AuditTrailPage />} />

                  {/* Projects */}
                  <Route path="projects" element={<Projects />} />
                  <Route
                    path="projects/dashboard"
                    element={<ProjectDashboard />}
                  />
                  <Route path="projects/kanban" element={<ProjectKanban />} />
                  <Route
                    path="projects/calendar"
                    element={<ProjectCalendar />}
                  />
                  <Route
                    path="projects/new"
                    element={<EnhancedProjectForm />}
                  />
                  <Route
                    path="projects/create"
                    element={<EnhancedProjectForm />}
                  />
                  <Route
                    path="projects/templates"
                    element={<ProjectTemplates />}
                  />
                  <Route
                    path="projects/templates/new"
                    element={<ProjectForm />}
                  />
                  <Route
                    path="projects/templates/:id"
                    element={<ProjectDetail />}
                  />
                  <Route
                    path="projects/templates/:id/edit"
                    element={<ProjectForm />}
                  />
                  <Route path="projects/reports" element={<ProjectReports />} />
                  <Route
                    path="projects/gantt"
                    element={<ProjectGanttReport />}
                  />
                  <Route
                    path="projects/analytics"
                    element={<ProjectAnalytics />}
                  />
                  <Route path="projects/archive" element={<ProjectArchive />} />
                  <Route path="projects/:id" element={<ProjectDetail />} />
                  <Route
                    path="projects/:id/edit"
                    element={<EnhancedProjectForm />}
                  />
                  <Route path="projects/:id/tasks" element={<ProjectTasks />} />
                  <Route path="projects/:id/team" element={<ProjectTeam />} />
                  <Route
                    path="projects/:id/timeline"
                    element={<ProjectTimeline />}
                  />
                  <Route
                    path="projects/:id/budget"
                    element={<ProjectBudget />}
                  />
                  <Route
                    path="projects/:id/settings"
                    element={<ProjectSettings />}
                  />

                  {/* Inventory */}
                  <Route path="inventory/*" element={<Inventory />} />

                  {/* HR */}
                  <Route path="hr/*" element={<HR />} />

                  {/* Finance */}
                  <Route path="finance/*" element={<Finance />} />

                  {/* Admin */}
                  <Route path="/admin/*" element={<Admin />} />
                  <Route path="/self-service/*" element={<SelfService />} />
                  <Route path="/collaboration/*" element={<Collaboration />} />
                  <Route path="/file-share/*" element={<FileShare />} />
                  <Route path="/email/*" element={<Email />} />

                  {/* Document Management */}
                  <Route
                    path="documents"
                    element={<EnhancedDocumentManagement />}
                  />

                  {/* Purchase Requests - Protected by permissions */}
                  <Route
                    path="purchase-requests"
                    element={
                      <PermissionRoute permissionId="purchase-dashboard">
                        <PurchaseRequestList />
                      </PermissionRoute>
                    }
                  />
                  <Route
                    path="purchase-requests/dashboard"
                    element={
                      <PermissionRoute permissionId="purchase-dashboard">
                        <PurchaseRequestDashboard />
                      </PermissionRoute>
                    }
                  />
                  <Route
                    path="purchase-requests/create"
                    element={
                      <PermissionRoute permissionId="purchase-create">
                        <PurchaseRequestKanban />
                      </PermissionRoute>
                    }
                  />
                  <Route
                    path="purchase-requests/:requestId"
                    element={
                      <PermissionRoute permissionId="purchase-create">
                        <PurchaseRequestDetail />
                      </PermissionRoute>
                    }
                  />
                  <Route
                    path="purchase-requests/pending-approval"
                    element={
                      <PermissionRoute permissionId="purchase-pending">
                        <ManagerApproval />
                      </PermissionRoute>
                    }
                  />
                  <Route
                    path="purchase-requests/procurement"
                    element={
                      <PermissionRoute permissionId="purchase-procurement">
                        <ProcurementApproval />
                      </PermissionRoute>
                    }
                  />
                  <Route
                    path="purchase-requests/finance"
                    element={
                      <PermissionRoute permissionId="purchase-finance">
                        <FinancePayment />
                      </PermissionRoute>
                    }
                  />
                  <Route
                    path="purchase-requests/history"
                    element={
                      <PermissionRoute permissionId="purchase-history">
                        <PurchaseRequestHistory />
                      </PermissionRoute>
                    }
                  />
                  <Route
                    path="purchase-requests/settings"
                    element={
                      <PermissionRoute permissionId="purchase-settings">
                        <PurchaseRequestSettings />
                      </PermissionRoute>
                    }
                  />

                  {/* GMD Routes */}
                  <Route
                    path="gmd/dashboard"
                    element={
                      <PermissionRoute permissionId="gmd-dashboard">
                        <GMDDashboard />
                      </PermissionRoute>
                    }
                  />
                  <Route
                    path="gmd/signature"
                    element={
                      <PermissionRoute permissionId="gmd-signature">
                        <GMDSignature />
                      </PermissionRoute>
                    }
                  />
                  <Route
                    path="gmd/pin"
                    element={
                      <PermissionRoute permissionId="gmd-pin">
                        <GMDPin />
                      </PermissionRoute>
                    }
                  />

                  <Route
                    path="purchase-requests/new"
                    element={
                      <PermissionRoute permissionId="purchase-create">
                        <PurchaseRequestForm />
                      </PermissionRoute>
                    }
                  />
                  <Route
                    path="purchase-requests/:requestId"
                    element={
                      <PermissionRoute permissionId="purchase-dashboard">
                        <PurchaseRequestDetail />
                      </PermissionRoute>
                    }
                  />

                  {/* Support Routes */}
                  <Route
                    path="support"
                    element={<Navigate to="/support/tickets" replace />}
                  />
                  <Route
                    path="support/all"
                    element={
                      <PermissionRoute permissionId="support-all">
                        <AllSupports />
                      </PermissionRoute>
                    }
                  />
                  <Route
                    path="support/tickets"
                    element={
                      <PermissionRoute permissionId="support-tickets">
                        <MyTickets />
                      </PermissionRoute>
                    }
                  />
                  <Route
                    path="support/create"
                    element={
                      <PermissionRoute permissionId="support-create">
                        <CreateTicket />
                      </PermissionRoute>
                    }
                  />
                </Route>

                {/* 404 */}
                <Route path="*" element={<NotFound />} />
              </Routes>
            </WebSocketProvider>
          </ManagerPendingActionsWrapper>
        </AuthProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
